import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Sparkles, Play, Pause, Volume2, VolumeX } from "lucide-react";
import ProtectedImage from "@/components/ProtectedImage";

// Static brand assets (bundled in /public) so they deploy identically to preview + production
const PROGRESSION_VIDEO_WEBM = "/media/progression.webm";
const PROGRESSION_VIDEO_MP4 = "/media/progression.mp4";
const STEP_POSTER = "/media/step4.jpg";

const STEPS = [
  {
    n: "01", title: "It starts with a rough sketch",
    file: "/media/step1.jpg",
    text: "Every custom caricature begins with loose pencil shapes — blocking in the head, proportions, and placement of the features. This is where I plan how to playfully exaggerate what makes you you, working straight from your photo.",
  },
  {
    n: "02", title: "Refining the features",
    file: "/media/step2.jpg",
    text: "Next I tighten the drawing by hand — carefully shaping the eyes, nose, and lips to capture a true likeness while keeping that fun, characterful caricature twist. This is the stage where the face really starts to feel alive.",
  },
  {
    n: "03", title: "Inking the linework",
    file: "/media/step3.jpg",
    text: "Once the pencil drawing is just right, I ink over it with bold, confident lines. The inking gives the piece its crisp, finished caricature style and makes every expressive detail pop.",
  },
  {
    n: "04", title: "The finished caricature",
    file: "/media/step4.jpg",
    text: "After erasing the pencil guides and adding the final details and signature, your one-of-a-kind hand-drawn caricature is ready — delivered to your inbox, mailed to your door, or both.",
  },
];

const CAPTIONS = [
  { label: "Rough sketch", sub: "blocking it in" },
  { label: "Refining features", sub: "finding the likeness" },
  { label: "Inking the lines", sub: "making it pop" },
  { label: "Finished!", sub: "one-of-a-kind" },
];

function ProgressionVideo() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [capIdx, setCapIdx] = useState(0);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { const p = v.play(); if (p) p.catch(() => {}); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const onTime = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const idx = Math.min(CAPTIONS.length - 1, Math.floor((v.currentTime / v.duration) * CAPTIONS.length));
    setCapIdx((prev) => (prev === idx ? prev : idx));
  };

  const cap = CAPTIONS[capIdx];

  return (
    <section className="mt-20" data-testid="process-video-section">
      {/* animated barber-pole ribbon */}
      <div className="ribbon-marquee h-4 rounded-full border-[3px] border-ink shadow-brut mb-8" aria-hidden="true" />

      <div className="text-center mb-8">
        <span className="tag-pill bg-bubble text-white wiggle inline-flex" data-testid="video-badge">
          <Sparkles className="w-3.5 h-3.5" /> Watch the magic
        </span>
        <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl mt-4">
          See a caricature come <span className="text-bubble">to life</span>
        </h2>
        <p className="text-ink/70 text-lg mt-3 max-w-xl mx-auto">
          Blank page to finished piece — every stroke drawn by hand. Here's the whole thing in a few seconds.
        </p>
      </div>

      <div className="relative mx-auto max-w-3xl">
        {/* floating sticker */}
        <div className="absolute -top-6 -left-4 sm:-left-8 z-20 float-y" aria-hidden="true">
          <div className="spin-slow bg-sun border-[3px] border-ink rounded-full w-20 h-20 sm:w-24 sm:h-24 grid place-items-center shadow-brut">
            <span className="font-display font-bold text-ink text-xs sm:text-sm text-center leading-tight -rotate-12 spin-slow-rev">
              HAND<br/>DRAWN
            </span>
          </div>
        </div>

        {/* video frame */}
        <div className="relative bg-white p-3 pb-4 border-[3px] border-ink rounded-3xl pop-glow rotate-1 hover:rotate-0 transition-transform">
          <div className="relative overflow-hidden rounded-2xl border-2 border-ink bg-cream-solid">
            <video
              ref={videoRef}
              autoPlay muted loop playsInline
              poster={STEP_POSTER}
              controlsList="nodownload"
              disablePictureInPicture
              onTimeUpdate={onTime}
              onContextMenu={(e) => e.preventDefault()}
              className="w-full h-auto block max-h-[70vh] object-contain bg-cream-solid"
              data-testid="progression-video"
            >
              <source src={PROGRESSION_VIDEO_WEBM} type="video/webm" />
              <source src={PROGRESSION_VIDEO_MP4} type="video/mp4" />
            </video>

            {/* progress dots */}
            <div className="absolute top-3 right-3 flex gap-1.5 z-10" aria-hidden="true">
              {CAPTIONS.map((_, i) => (
                <span key={i} className={`w-2.5 h-2.5 rounded-full border-2 border-ink transition-colors ${i <= capIdx ? "bg-bubble" : "bg-white/70"}`} />
              ))}
            </div>

            {/* animated step caption */}
            <div key={capIdx} className="caption-pop absolute top-3 left-3 z-10" data-testid="video-caption">
              <div className="bg-sun border-[3px] border-ink rounded-xl shadow-brut px-3 py-2 flex items-center gap-2 max-w-[75%]">
                <span className="font-display font-bold text-ink bg-white border-2 border-ink rounded-lg w-7 h-7 grid place-items-center text-sm shrink-0">
                  {capIdx + 1}
                </span>
                <span className="leading-tight">
                  <span className="block font-display font-bold text-ink text-sm sm:text-base">{cap.label}</span>
                  <span className="block text-ink/70 text-xs font-body">{cap.sub}</span>
                </span>
              </div>
            </div>
            {/* custom controls */}
            <div className="absolute bottom-3 right-3 flex gap-2 z-10">
              <button
                onClick={togglePlay}
                aria-label={playing ? "Pause video" : "Play video"}
                className="brut-btn bg-white !p-3 !shadow-brut"
                data-testid="video-play-toggle"
              >
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              <button
                onClick={toggleMute}
                aria-label={muted ? "Unmute video" : "Mute video"}
                className="brut-btn bg-sun !p-3 !shadow-brut"
                data-testid="video-mute-toggle"
              >
                {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* corner accents */}
        <div className="absolute -bottom-4 -right-3 z-0 bg-sky border-[3px] border-ink rounded-2xl w-16 h-16 -rotate-6 hidden sm:block" aria-hidden="true" />
      </div>
    </section>
  );
}

export default function ProcessPage() {
  return (
    <div className="max-w-5xl mx-auto px-5 py-14" data-testid="process-page">
      <Helmet>
        <title>How a Custom Caricature Is Made: From Photo to Finished Art | Art by Jason Caricatures</title>
        <meta name="description" content="See the caricature commission process step by step — from rough sketch to inked, finished art. Learn how to order a custom hand-drawn caricature from your photo with Art by Jason." />
      </Helmet>

      <div className="text-center mb-14">
        <span className="tag-pill bg-sun mb-4">Behind the pen</span>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl">How a caricature is made</h1>
        <p className="text-ink/70 text-lg mt-4 max-w-2xl mx-auto">
          Curious what goes into your custom caricature? Here's the full commission process — every piece is hand-drawn from your photo, sketch to finished art.
        </p>
      </div>

      <div className="space-y-16">
        {STEPS.map((s, i) => (
          <div key={s.n} className={`grid md:grid-cols-2 gap-8 items-center ${i % 2 ? "md:[direction:rtl]" : ""}`} data-testid={`process-step-${i + 1}`}>
            <div className="[direction:ltr]">
              <div className="bg-white p-3 pb-4 border-[3px] border-ink rounded-2xl shadow-brutxl -rotate-1 hover:rotate-0 transition-transform">
                <ProtectedImage>
                  <img src={s.file} alt={`Caricature process step ${s.n}: ${s.title}`} draggable={false}
                    className="w-full h-72 sm:h-96 object-contain bg-cream-solid rounded-lg border-2 border-ink" />
                </ProtectedImage>
              </div>
            </div>
            <div className="[direction:ltr]">
              <span className="font-display font-bold text-6xl text-bubble/30">{s.n}</span>
              <h2 className="font-display font-bold text-2xl md:text-3xl -mt-3">{s.title}</h2>
              <p className="text-ink/70 text-base mt-3 leading-relaxed">{s.text}</p>
            </div>
          </div>
        ))}
      </div>

      <ProgressionVideo />

      <div className="brut-card p-8 mt-16 text-center bg-grape/15">
        <h2 className="font-display font-bold text-2xl sm:text-3xl">Ready for your own?</h2>
        <p className="text-ink/70 mt-2 mb-5">Send me a photo and I'll turn your favorite person into a one-of-a-kind caricature.</p>
        <Link to="/order" className="brut-btn bg-bubble text-white text-lg" data-testid="process-order-cta">
          Order your caricature <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </div>
  );
}
